/**
 * A doc de um arquivo, portada de app.js:544-605.
 *
 * Duas decisões do painel atual que ficam: o histórico é consulta, não leitura
 * principal, então fica recolhido e abaixo do texto (senão a doc começa depois
 * de doze versões); e o diff entre versões abre no lugar, sem trocar de tela,
 * porque a pergunta é "o que mudou aqui" e não "abra outra página".
 *
 * O que muda: a chave do documento agora está na URL (`?key=`). No painel atual
 * `openDoc` não mexia na barra de endereços, então não havia como mandar "olha a
 * doc deste arquivo" por mensagem — era o defeito mais citado do painel.
 */
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { postar } from '../api/cliente';
import { useDiffDaDoc, useDoc, useHistoricoDaDoc } from '../api/consultas';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { copiar, useRecado } from '../componentes/Recado';
import { md, semNotasDoTime } from '../md/markdown';
import { CorpoComNotas } from '../notas/Notas';
import { CabecaDoArquivo } from './CabecaDoArquivo';

export function Doc({ slug }: { slug: string }) {
  const [parametros] = useSearchParams();
  const chave = parametros.get('key') || '';
  const avisar = useRecado();
  const doc = useDoc(slug, chave);
  const historico = useHistoricoDaDoc(slug, chave);
  const [diff, setDiff] = useState<{ para: number } | null>(null);
  const [propondo, setPropondo] = useState(false);

  if (!chave) {
    return (
      <>
        <div className="crumb"><b>{slug}</b><span className="sep">/</span>documentação</div>
        <Vazio titulo="Escolha um arquivo na barra lateral">
          Esta aba mostra a doc de uma unidade do acervo. Clique num arquivo à esquerda, use a
          busca do topo ou a paleta (<span className="kbd">Ctrl</span>+<span className="kbd">K</span>)
          para chegar direto — a unidade escolhida entra na URL e o link pode ser compartilhado.
        </Vazio>
      </>
    );
  }

  if (doc.isPending) return <Carregando rotulo={`a doc de ${chave}`} />;
  if (doc.error) {
    return <Falha erro={doc.error} rotulo={`a doc de ${chave}`} aoRepetir={doc.refetch} />;
  }
  // a rota responde 200 com `{error}` quando a unidade não tem versão: é
  // resposta, não falha, e por isso vira estado vazio explicado
  if (doc.data?.error) {
    return (
      <>
        <div className="crumb"><b>{slug}</b><span className="sep">/</span>{chave}</div>
        <Vazio titulo="Este arquivo não tem documentação no acervo" comando={`eng reprocess ${slug}`}>
          O índice conhece <code>{chave}</code>, mas nenhuma versão de doc foi gerada para ele. Isso
          acontece quando o arquivo entrou depois do último ingest completo, ou quando o ingest
          falhou nele.
        </Vazio>
      </>
    );
  }

  const d = doc.data!;
  const unidade = d.unit_key || chave;
  const versoes = historico.data || [];

  return (
    <>
      <div className="crumb">
        <b>{slug}</b><span className="sep">/</span>{unidade}
        <button
          type="button"
          className="btn ghost sm"
          style={{ marginLeft: 'auto' }}
          onClick={async () => {
            avisar(await copiar(`eng doc ${unidade}`)
              ? 'comando copiado — cole no terminal do clone'
              : 'não consegui copiar — selecione o texto e use Ctrl+C');
          }}
        >
          copiar <code>eng doc</code>
        </button>
      </div>

      <CabecaDoArquivo
        slug={slug}
        chave={unidade}
        docSha={d.source_sha}
        aoPropor={() => setPropondo(true)}
      />

      <CorpoComNotas
        slug={slug}
        unidade={unidade}
        docSha={d.source_sha}
        corpo={d.body_md}
        rotuloDaFaixa="neste arquivo"
        vazioDoCorpo={(
          <Vazio titulo="A doc deste arquivo está vazia" comando={`eng reprocess ${slug}`}>
            Existe versão registrada, mas sem texto — em geral o modelo devolveu vazio para um
            arquivo muito pequeno ou só de tipos. Um reprocess costuma resolver; se não, escreva
            você mesmo uma nota explicando o papel do arquivo.
          </Vazio>
        )}
      />

      {versoes.length > 1 && (
        <details className="doc-history">
          <summary className="dh-title">
            Histórico da doc · {versoes.length} versões — o que mudou e em qual commit
          </summary>
          {versoes.map((v, i) => (
            <div key={v.version} className={`dh-row${i === 0 ? ' current' : ''}`}>
              <span className="dh-v">v{v.version}</span>
              <div className="dh-mid">
                <div className="dh-sum">{v.change_summary || '(sem resumo)'}</div>
                <div className="dh-meta">
                  {(v.source_sha || '').slice(0, 8)} · {v.mode} · {(v.generated_at || '').slice(0, 10)}
                </div>
              </div>
              {v.version > 1 && (
                <button type="button" className="btn ghost sm" onClick={() => setDiff({ para: v.version })}>
                  ver diff
                </button>
              )}
            </div>
          ))}
        </details>
      )}

      <div id="diff-box">
        {propondo && (
          <FormularioDeProposta
            slug={slug}
            chave={unidade}
            aoFechar={() => setPropondo(false)}
          />
        )}
        {diff && (
          <CaixaDeDiff slug={slug} chave={unidade} para={diff.para} aoFechar={() => setDiff(null)} />
        )}
      </div>

      <div className="prov">
        <div className="k"><b>unidade</b><span>{unidade}</span></div>
        <div className="k"><b>commit</b><span>{(d.source_sha || '').slice(0, 8)}</span></div>
        <div className="k"><b>versão</b><span>v{d.version || 1}</span></div>
        <div className="k"><b>gerado por</b><span>{d.generated_by || '—'}</span></div>
        <div className="k"><b>tokens</b><span>{d.token_cost || 0}</span></div>
      </div>
    </>
  );
}

function CaixaDeDiff({ slug, chave, para, aoFechar }: {
  slug: string;
  chave: string;
  para: number;
  aoFechar: () => void;
}) {
  const consulta = useDiffDaDoc(slug, chave, para);

  if (consulta.isPending) return <Carregando rotulo={`comparando com a versão ${para}…`} />;
  // a rota pode responder 200 sem diff (versão sem par); ausência de code_diff é
  // falha da comparação, e o painel atual dizia isso em vez de mostrar vazio
  if (consulta.error || !consulta.data?.code_diff) {
    return (
      <Falha
        erro={consulta.error || 'a API não devolveu o diff'}
        rotulo="a comparação entre versões"
        aoRepetir={consulta.refetch}
      />
    );
  }

  const d = consulta.data;
  const linhas = d.code_diff!.lines;
  return (
    <div className="diff-card">
      <div className="diff-head">
        <b>Diff v{d.from} → v{d.to}</b>{' '}
        <span className="muted">
          +{d.code_diff!.added} −{d.code_diff!.removed} linhas · commit {(d.source_sha || '').slice(0, 8)}
        </span>{' '}
        <button type="button" className="btn ghost sm" onClick={aoFechar}>fechar</button>
      </div>
      <div className="diff-summary"><b>Resumo IA:</b> {d.change_summary || '(sem resumo)'}</div>
      <div className="diff-cols">
        <div className="diff-col">
          <div className="diff-col-t">Código</div>
          <pre className="diff">
            {linhas.length
              ? linhas.map((x, i) => (
                <div key={i} className={`dl${x.t === '+' ? ' add' : x.t === '-' ? ' del' : ''}`}>
                  <span className="dl-g">{x.t}</span>{x.l}
                </div>
              ))
              : (
                <div className="muted" style={{ fontSize: 12.5, padding: 8 }}>
                  O código não mudou entre estas duas versões — a doc foi regerada sem mudança no arquivo.
                </div>
              )}
          </pre>
        </div>
      </div>
    </div>
  );
}

/**
 * Humano propondo pelo painel (knowledge.js:110-140): mesma fila da IA, mesma
 * revisão. Editar o acervo direto seria mais rápido e é justamente o que não se
 * quer — quem aprova é o dono do módulo.
 */
function FormularioDeProposta({ slug, chave, aoFechar }: {
  slug: string;
  chave: string;
  aoFechar: () => void;
}) {
  const avisar = useRecado();
  const doc = useDoc(slug, chave);
  const base = semNotasDoTime(doc.data?.body_md);
  const [texto, setTexto] = useState(base);
  const [porque, setPorque] = useState('');
  const [especie, setEspecie] = useState('melhoria');
  const [enviando, setEnviando] = useState(false);

  const enviar = async () => {
    if (!texto.trim()) { avisar('escreva o texto proposto'); return; }
    setEnviando(true);
    try {
      await postar(`/api/${slug}/proposals`, {
        unit_key: chave, body_md: texto.trim(), rationale: porque.trim() || null, kind: especie,
      });
      avisar('proposta enviada para revisão');
      aoFechar();
    } catch (e) {
      avisar(e instanceof Error ? e.message : String(e));
    } finally { setEnviando(false); }
  };

  return (
    <div className="prop open pending" style={{ marginTop: 12 }}>
      <div className="prop-head">
        <span className="tag melhoria">nova proposta</span>
        <span className="unit">{chave}</span>
        <span className="who">vai para a fila de revisão como qualquer proposta da IA</span>
      </div>
      <div className="prop-body">
        <div className="sbs">
          <div className="col">
            <div className="col-t">no acervo hoje (v{doc.data?.version || 1})</div>
            <div className="col-b" dangerouslySetInnerHTML={{ __html: md(base) }} />
          </div>
          <div className="col">
            <div className="col-t">seu texto</div>
            <textarea value={texto} onChange={(e) => setTexto(e.target.value)} aria-label="texto proposto" />
          </div>
        </div>
        <div className="prop-acts">
          <input
            value={porque}
            onChange={(e) => setPorque(e.target.value)}
            placeholder="por que mudar (vai para o changelog da versão)"
          />
          <select value={especie} onChange={(e) => setEspecie(e.target.value)} aria-label="tipo da proposta">
            <option value="melhoria">melhoria</option>
            <option value="correcao">correção (a doc estava errada)</option>
          </select>
          <button type="button" className="btn" disabled={enviando} onClick={() => void enviar()}>
            {enviando ? 'enviando…' : 'Enviar para revisão'}
          </button>
          <button type="button" className="btn ghost" onClick={aoFechar}>cancelar</button>
        </div>
      </div>
    </div>
  );
}
